import React, { useState } from 'react';
import { Button, Alert, Box, Container, Header, SpaceBetween, Modal } from '@amzn/awsui-components-console';
import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import { SDS_APIGATEWAY_ENDPOINT } from 'src/config';
import { WorkflowDefinition } from '@amzn/snow-nxt-workflow-service-typescript-client';
import { logger } from 'src/utils/logger';
import ErrorAlert from 'src/components/commonComponents/ErrorAlert';
import { EXCEL_SHEET_CONTENT_TYPE } from 'src/constants/BulkUploadConstants';

interface BulkExecutionDownloadTemplateStepProps {
	workflowDefinition: WorkflowDefinition;
}

const BulkExecutionDownloadTemplateStep: React.FC<BulkExecutionDownloadTemplateStepProps> = (props) => {
	const [workflowSupported, setWorkflowSupported] = useState(true);
	const [isDownloading, setIsDownloading] = useState(false);

	const handleDownloadClick = async () => {
		setIsDownloading(true);

		try {
			const res = await callSNOWDataService({
				path: `/bulk/template/${props.workflowDefinition.workflowId}`,
				method: 'GET',
				url: SDS_APIGATEWAY_ENDPOINT,
				queryParams: {},
			} as any);

			if (!res || !res.fileContent) {
				throw new Error('Bulk execution function is not supported for the selected workflow. Please reach out to snow-dev@ for support.');
			}

			const byteCharacters = atob(res.fileContent);
			const byteNumbers = new Array(byteCharacters.length);

			for (let i = 0; i < byteCharacters.length; i++) {
				byteNumbers[i] = byteCharacters.charCodeAt(i);
			}
			
			const byteArray = new Uint8Array(byteNumbers);

			const blob = new Blob([byteArray], {
				type: EXCEL_SHEET_CONTENT_TYPE,
			});

			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');

			link.href = url;
			link.setAttribute('download', `template-${props.workflowDefinition.workflowDefinitionName}.xlsx`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (error) {
			const errorMessage = (error as Error).message || 'An unexpected error occurred while downloading the template.';
			setWorkflowSupported(false);
			logger.error(errorMessage);
		} finally {
			setIsDownloading(false);
		}
	};

	return (
		<Container header={<Header variant="h2">Workflow Type: {props.workflowDefinition ? props.workflowDefinition.workflowDefinitionName : 'No Workflow Definition Available'}</Header>}>
			<SpaceBetween size="m">
				<Box>
					<SpaceBetween size={'s'} direction={'horizontal'} alignItems={'center'}>
						<Button variant="primary" onClick={handleDownloadClick} iconName={'download'} download={true} iconAlign={'right'} loading={isDownloading}>
							Download Template
						</Button>
					</SpaceBetween>
				</Box>

				<Modal
					onDismiss={() => (location.href = '../#/executeWorkflows')}
					visible={!workflowSupported}
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button variant="primary" onClick={() => (location.href = '../#/executeWorkflows')}>Close</Button>
							</SpaceBetween>
						</Box>
					}
					header="Download Template Failed"
				>
					<ErrorAlert dependentProp={undefined} children={'Bulk execution function is not supported for the selected workflow. Please reach out to snow-dev@ for support.'} />
				</Modal>

				<Alert type="info" header="Important Instructions">
					<p>Please follow the guidelines below while using the Excel template:</p>
					<ul>
						<li>Use the downloaded template as it is and just add your requests.</li>
						<li>Do not change the structure of the columns.</li>
						<li>The template contains regex validations for the values of the columns. Ensure that your inputs match these validations.</li>
						<li>Add each request as a new row in the template.</li>
					</ul>
				</Alert>
			</SpaceBetween>
		</Container>
	);
};

export default BulkExecutionDownloadTemplateStep;
